import { AutoCompleteVirtualize } from '@openx/components-core/lib/AutoComplete/AutoCompleteVirtualize.tsx';
import { CustomSizeInput } from '@openx/components-core/lib/CustomSizeInput/CustomSizeInput.tsx';
import { type InventoryAndContentState, InventoryContentOption } from '@openx/types/targeting/inventoryAndContent.ts';
import { noop } from '@openx/utils/noop.ts';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AnyAction } from 'redux';
import { useTargetingContext } from '../../../../utils/context/TargetingContext.tsx';
import { setComparisonOpValue } from '../../../shared/InventoryAndContent/inventoryHelpers.ts';
import type { TargetingItemsProps } from '../../../types.ts';

const CUSTOM_ID = 'custom';

export const AdUnitSizeDropdown = ({
  targetingParams,
  dispatchInventory = noop,
}: TargetingItemsProps<InventoryAndContentState>) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const [customEnabled, setCustomEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const { val } = targetingParams[InventoryContentOption.ADUNIT_SIZE];
  const { usePmpSizesFetch } = useTargetingContext();

  const { isSizesLoading, sizes } = usePmpSizesFetch();

  const sizesList = useMemo(() => {
    const CUSTOM_OPTION = {
      id: CUSTOM_ID,
      name: t('Add Custom Size'),
    };
    return [CUSTOM_OPTION, ...Object.values(sizes)];
  }, [t, sizes]);

  const filterOptions = useCallback(
    options => {
      return options.filter(opt => {
        return (
          (opt.name.includes(value) || opt.id === CUSTOM_ID) &&
          !targetingParams[InventoryContentOption.ADUNIT_SIZE].val.find(item => item === opt.id)
        );
      });
    },
    [targetingParams, value]
  );

  const onChange = useCallback(
    (value: { id: string }) => {
      if (value.id === CUSTOM_ID) {
        setCustomEnabled(true);
        setOpen(false);
        return;
      }
      dispatchInventory({
        payload: [...new Set([...val, value.id])],
        type: setComparisonOpValue[InventoryContentOption.ADUNIT_SIZE],
      } as unknown as AnyAction);
    },
    [dispatchInventory, val]
  );

  return (
    <>
      <AutoCompleteVirtualize
        fullWidth
        textFieldProps={{
          label: t('Ad Unit Size'),
          placeholder: t('[ Select an Ad Unit Size ]'),
        }}
        disableClearable
        disableCloseOnSelect
        renderOptions={{
          dataTest: 'type-option',
        }}
        options={sizesList}
        getOptionLabel={option => option?.name || ''}
        filterOptions={filterOptions}
        onChange={(_, value) => onChange(value)}
        inputValue={value}
        data-test="adunit-size-dropdown"
        loading={isSizesLoading}
        disabled={isSizesLoading}
        onClose={() => {
          setOpen(false);
          setValue('');
        }}
        onInputChange={(_e, value, reason) => {
          (reason === 'input' || reason === 'clear') && setValue(value);
        }}
        onOpen={() => setOpen(true)}
        value={undefined}
        open={open}
      />
      {customEnabled && (
        <CustomSizeInput onAdd={itemText => onChange({ id: itemText })} onCancel={() => setCustomEnabled(false)} />
      )}
    </>
  );
};
